import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Heart, Globe, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { favoritesService } from '../services/favoritesService';
import EventCard from '../components/EventCard';
import Footer from '../components/Footer';

const Favorites: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const [favoriteEvents, setFavoriteEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadFavorites();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadFavorites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const events = await favoritesService.getFavorites();
      setFavoriteEvents(events);
    } catch (err: any) {
      console.error('Error loading favorites:', err);
      setError(err.message || 'Failed to load favorites');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFavorite = async (eventId: string) => {
    try {
      await favoritesService.removeFavorite(eventId);
      // Remove from local state
      setFavoriteEvents(prev => prev.filter(event => event.id !== eventId));
    } catch (err: any) {
      console.error('Error removing favorite:', err);
      setError(err.message || 'Failed to remove favorite');
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/event/${eventId}`);
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8 liquid-stagger-children">
            <div className="flex justify-center items-center space-x-3 mb-4">
              <Heart size={28} className="liquid-glow" style={{color: 'var(--liquid-text-primary)'}} />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold liquid-glow-strong" style={{color: 'var(--liquid-text-primary)'}}>
                {t('navigation.favorites') || 'My Favorites'}
              </h1>
            </div>
            <p className="text-lg max-w-2xl mx-auto leading-relaxed" style={{color: 'var(--liquid-text-muted)'}}>
              {t('favorites.description', 'Events you\'ve saved for later')}
            </p>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={48} className="animate-spin text-blue-400" />
            </div>
          ) : error ? (
            /* Error State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="liquid-glow text-red-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>Error loading favorites</h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                {error}
              </p>
              <button
                onClick={loadFavorites}
                className="liquid-glass-button px-6 py-3 rounded-2xl text-white font-medium liquid-glow hover:scale-105 transition-transform"
              >
                Try Again
              </button>
            </div>
          ) : !isAuthenticated ? (
            /* Not Authenticated State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="liquid-glow" style={{color: 'var(--liquid-text-muted)'}} />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>Sign in to view favorites</h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                Please sign in to save and view your favorite events.
              </p>
              <button
                onClick={openLogin}
                className="liquid-glass-button px-6 py-3 rounded-2xl text-white font-medium liquid-glow hover:scale-105 transition-transform"
              >
                Sign In
              </button>
            </div>
          ) : favoriteEvents.length > 0 ? (
            /* Favorites Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {favoriteEvents.map((event) => (
                <div key={event.id} className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-3xl liquid-glass-card hover:scale-105 transition-all duration-500 shadow-2xl">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      
                      {/* Favorite Button */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFavorite(event.id);
                        }}
                        className="absolute top-4 right-4 w-10 h-10 liquid-glass-button rounded-full flex items-center justify-center hover:scale-110 transition-transform liquid-glow"
                        style={{color: 'var(--liquid-text-primary)'}}
                        title="Remove from favorites"
                      >
                        <Heart 
                          size={18} 
                          className="text-red-400 fill-current"
                        />
                      </button>
                    </div>

                    {/* Event Details */}
                    <div className="p-6">
                      <h3 className="font-bold text-lg mb-3 line-clamp-2" style={{color: 'var(--liquid-text-primary)'}}>{event.title}</h3>
                      
                      <div className="space-y-2 mb-4 text-sm" style={{color: 'var(--liquid-text-muted)'}}>
                        <div>{event.date} • {event.time || 'TBA'}</div>
                        <div>{event.venue || 'TBA'}</div>
                        <div>{event.ticketsSold || 0} attending</div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>${event.price}</div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event.id);
                          }}
                          className="liquid-glass-button px-6 py-3 rounded-2xl text-white font-medium liquid-glow hover:scale-105 transition-transform"
                        >
                          View Event
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-16">
              <div className="w-24 h-24 liquid-glass rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="liquid-glow" style={{color: 'var(--liquid-text-muted)'}} />
              </div>
              <h3 className="text-2xl font-bold mb-4 liquid-glow" style={{color: 'var(--liquid-text-primary)'}}>No favorites yet</h3>
              <p className="mb-8 max-w-md mx-auto" style={{color: 'var(--liquid-text-muted)'}}>
                Start exploring events and save your favorites here for easy access later.
              </p>
              <button
                onClick={() => navigate('/')}
                className="liquid-glass-button px-6 py-3 rounded-2xl text-white font-medium liquid-glow hover:scale-105 transition-transform"
              >
                Discover Events
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Favorites; 